import { defineStore } from 'pinia';
import { useI18n } from 'vue-i18n';
import { computed, shallowRef, ref, triggerRef } from 'vue';
import type {
	ExtraWorkDetailsQuery,
	ExtraWorkOverviewQuery,
	ExtraWorkRequest,
	ExtraWorkResponse,
	ExtraWorkOverviewResponse,
	ExtraWorkDetailsResponse,
	ExtraWorkOverviewListResponse,
	ExtraWorkDetailsListResponse,
	ReportStatus,
} from '@/rennovation/planning-view/types/api/extra-work-response';
import { useReportApi } from '@/rennovation/common/services/api/useReportApi';
import { useDutyStore } from '@/rennovation/planning-view/store/useDutyStore';
import { DutyType } from '@/rennovation/planning-view/types/api/single-duty-response';

export const useReportStore = defineStore('report', () => {
	const { t } = useI18n();
	const reportApi = useReportApi();

	// ============================================
	// STATE
	// ============================================
	const reportsById = shallowRef<Record<string, ExtraWorkResponse>>({});
	const overviewReports = ref<ExtraWorkOverviewResponse[]>([]);
	const detailReports = ref<ExtraWorkDetailsResponse[]>([]);

	const pagination = ref({
		page: 1,
		size: 10,
		total: 0,
		totalPages: 0,
	});
	const fetchError = ref<string | null>(null);
	const isFetching = ref<boolean>(false);

	const activeDutyId = ref<string | null>(null);
	const activeDuty = ref<DutyType | null>(null);

	// ============================================
	// GETTERS
	// ============================================

	/**
	 * Returns all reports as a flat array of ExtraWorkResponse objects
	 */
	const dutyReports = computed((): ExtraWorkResponse[] => Object.values(reportsById.value));

	// ============================================
	// ACTIONS
	// ============================================

	/**
	 * Loads reports for a specific duty and replaces the state.
	 * Defaults to the activeDutyId if no ID is passed.
	 */
	async function loadReportsByDuty(dutyId: string | null = activeDutyId.value): Promise<ExtraWorkResponse> {
		isFetching.value = true;
		fetchError.value = null;

		if (!dutyId) {
			isFetching.value = false;
			return;
		}

		try {
			const response = await reportApi.getReportsByDuty(dutyId);
			const newReports: Record<string, ExtraWorkResponse> = {};

			response.forEach((item: ExtraWorkResponse) => {
				const id = item._id ?? item.id; // API returns _id; normalize to also set id
				item.id = id;
				newReports[id] = item;
			});

			reportsById.value = newReports;
		} catch (error: any) {
			fetchError.value = error.message || 'Failed to load reports';
			console.error('Error fetching duty reports:', error);
		} finally {
			isFetching.value = false;
		}
	}

	async function loadOverviewReports(params: ExtraWorkOverviewQuery): Promise<ExtraWorkOverviewListResponse> {
		isFetching.value = true;
		fetchError.value = null;

		try {
			const response: ExtraWorkOverviewListResponse = await reportApi.getOverviewReports(params);

			overviewReports.value = response.data;
			pagination.value = response.pagination;
			return response;
		} catch (error) {
			fetchError.value = error instanceof Error ? error.message : t('unknown_error');
			throw error;
		} finally {
			isFetching.value = false;
		}
	}

	async function loadDetailReports(params: ExtraWorkDetailsQuery): Promise<ExtraWorkDetailsListResponse> {
		isFetching.value = true;
		fetchError.value = null;

		try {
			const response: ExtraWorkDetailsListResponse = await reportApi.getDetailReports(params);

			detailReports.value = response.data;
			pagination.value = response.pagination;
			return response;
		} catch (error) {
			fetchError.value = error instanceof Error ? error.message : t('unknown_error');
			throw error;
		} finally {
			isFetching.value = false;
		}
	}

	/**
	 * Creates a new report and adds it to the local state
	 */
	async function createNewReport(reportPayload: Partial<ExtraWorkRequest>): Promise<ExtraWorkResponse> {
		isFetching.value = true;
		fetchError.value = null;
		try {
			const response = await reportApi.createReport(reportPayload);

			const id = response._id ?? response.id;
			response.id = id;
			reportsById.value[id] = response;
			triggerRef(reportsById);

			return response;
		} catch (err: any) {
			fetchError.value = err.response?.data?.message || err.message || 'Failed to create report';
			console.error('Error creating report:', err);
		} finally {
			isFetching.value = false;
		}
	}

	/**
	 * Edits an existing report and updates the local record
	 */
	async function editReport(reportID: string, payload: Partial<ExtraWorkRequest>): Promise<ExtraWorkResponse> {
		isFetching.value = true;
		fetchError.value = null;
		try {
			const response = await reportApi.editReport(reportID, payload);

			if (reportsById.value[reportID]) {
				reportsById.value[reportID] = response;
				triggerRef(reportsById);
			}

			return response;
		} catch (err: any) {
			fetchError.value = err.response?.data?.message || err.message || 'Failed to edit report';
			console.error('Error editing report:', err);
		} finally {
			isFetching.value = false;
		}
	}

	/**
	 * Deletes a report via API and updates local state
	 */
	async function deleteReport(reportID: string, changeComment: string | null): Promise<void> {
		isFetching.value = true;
		fetchError.value = null;

		try {
			await reportApi.deleteReport(reportID, changeComment);

			if (reportsById.value[reportID]) {
				delete reportsById.value[reportID];
				triggerRef(reportsById);
			}
		} catch (error: any) {
			fetchError.value = error.response?.data?.message || error.message || 'Failed to delete report';
			console.error('Error deleting report:', error);
		} finally {
			isFetching.value = false;
		}
	}

	async function setActiveDutyId(dutyId: string): Promise<void> {
		if (activeDutyId.value === dutyId && activeDuty.value) {
			return;
		}
		await setActiveDuty(dutyId);
	}

	/**
	 * Sets the active duty ID, fetches full duty details from the duty store,
	 * and then loads associated reports.
	 */
	async function setActiveDuty(dutyId: string): Promise<void> {
		isFetching.value = true;
		fetchError.value = null;
		activeDutyId.value = dutyId;

		reportsById.value = {};
		activeDuty.value = null;

		try {
			const dutyStore = useDutyStore();
			activeDuty.value = await dutyStore.fetchDutyByID(dutyId);

			await loadReportsByDuty(dutyId);
		} catch (error: any) {
			fetchError.value = error.response?.data?.message || error.message || 'Failed to fetch duty details';
		} finally {
			isFetching.value = false;
		}
	}

	return {
		overviewReports,
		detailReports,
		pagination,

		loadOverviewReports,
		loadDetailReports,
		// State
		activeDutyId,
		activeDuty,
		fetchError,
		isFetching,
		// Getters
		dutyReports,
		// Actions
		setActiveDuty,
		setActiveDutyId,
		createNewReport,
		editReport,
		deleteReport,
		loadReportsByDuty,
	};
});
